// src/services/eventReviewAI.ts

import { Event } from '../types/models'; // Assuming Event type is in models.ts
import { Timestamp } from 'firebase/firestore'; // Ensure Timestamp is imported

export type ReviewRecommendation = 'approve' | 'reject' | 'manual_review';

export interface RecommendationResult {
  recommendation: ReviewRecommendation;
  reason: string;
}

/**
 * Provides an AI-like recommendation for event review based on event data.
 * This is a basic rule-based implementation.
 * @param event The event object to review.
 * @returns A promise resolving to a RecommendationResult.
 */
export const getReviewRecommendation = async (event: Event): Promise<RecommendationResult> => {
  // Simulate a small delay for async operation
  await new Promise(resolve => setTimeout(resolve, 300));

  // --- Basic Checks ---
  if (!event.title || event.title.trim() === '') {
    return {
      recommendation: 'reject',
      reason: 'Missing or empty title.'
    };
  }

  if (!event.description || event.description.trim() === '') {
    return {
      recommendation: 'manual_review',
      reason: 'Missing or empty description. Requires manual review.'
    };
  }

  if (!event.coverImage || event.coverImage.trim() === '') {
     if (!event.imageUrl || event.imageUrl.trim() === '') { // Fallback check
        return {
          recommendation: 'reject',
          reason: 'Missing or empty cover image.'
        };
     }
  }

  // --- Advanced Checks ---

  // Check for valid start date/time
  if (!event.startDateTime || !event.startDateTime.toDate) {
      return {
          recommendation: 'reject',
          reason: 'Missing or invalid start date/time.'
      };
  }

  // Check for end date/time and its logical order
  if (!event.endDateTime || !event.endDateTime.toDate) {
       return {
           recommendation: 'manual_review',
           reason: 'Missing or invalid end date/time. Requires manual review.'
       };
  } else {
    // Ensure end date is after start date
    const startDate = event.startDateTime.toDate();
    const endDate = event.endDateTime.toDate();
    if (endDate.getTime() < startDate.getTime()) {
      return {
        recommendation: 'reject',
        reason: 'End date/time is before start date/time.'
      };
    }
  }

  // Basic location check
  if (!event.location || !event.location.venue || !event.location.city) {
      return {
          recommendation: 'reject',
          reason: 'Missing or incomplete location details (venue or city).'
      };
  }

  // Check for category
  if (!event.category || event.category.trim() === '') {
      return {
          recommendation: 'manual_review',
          reason: 'Missing event category. Requires manual review.'
      };
  }

  // Ticket tiers validation
  if (!event.ticketTiers || event.ticketTiers.length === 0) {
       return {
           recommendation: 'manual_review',
           reason: 'No ticket tiers defined. Requires manual review.'
       };
  } else {
      for (const tier of event.ticketTiers) {
          if (!tier.name || tier.name.trim() === '') {
              return {
                  recommendation: 'reject',
                  reason: `Ticket tier "${tier.name || 'Unnamed'}" has a missing or empty name.`
              };
          }
          if (typeof tier.price !== 'number' || tier.price < 0) { // Price can be 0 for free events
              return {
                  recommendation: 'reject',
                  reason: `Ticket tier "${tier.name}" has an invalid price.`
              };
          }
          if (typeof tier.quantity !== 'number' || !Number.isInteger(tier.quantity) || tier.quantity <= 0) {
              return {
                  recommendation: 'reject',
                  reason: `Ticket tier "${tier.name}" has an invalid or non-positive quantity.`
              };
          }
      }
  }

  // If all checks pass
  return {
    recommendation: 'approve',
    reason: 'All essential details appear to be present and valid based on automated checks.'
  };
}; 